import {Component, effect, computed, inject, signal, OnInit, ViewChild, DestroyRef} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {SliderFormComponent} from "./slider-form/slider-form.component";
import {NotificationsModel} from "../../core/notifications/notifications.model";
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {SliderManagementService} from "./services/slider-management.service";
import {MatTableModule, MatTableDataSource} from '@angular/material/table';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSortModule, MatSort} from '@angular/material/sort';
import {MatPaginatorModule, MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatIconModule} from '@angular/material/icon';
import {MatPaginatorIntlDe} from "../../../assets/i18n/mat-paginator-intl.de";
import {ConfirmationDialogModel} from "../../core/deliveries/models/confirmation.dialog.model";
import {ConfirmationDialogComponent} from "../../shared/confirmation-dialog/confirmation-dialog.component";
import {SliderManagementModel} from "./models/slider-management.model";
import {PreviewDialogComponent} from "../../shared/preview-dialog/preview-dialog.component";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, switchMap, tap, finalize} from 'rxjs/operators';

export interface TableRow {
  index: number;
  title: string;
  message: string;
  date: string;
  status: string;
}

@Component({
  selector: 'app-slider-management',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule
  ],
  providers: [
    {provide: MatPaginatorIntl, useClass: MatPaginatorIntlDe}
  ],
  templateUrl: './slider-management.component.html',
  styleUrl: './slider-management.component.scss'
})
export class SliderManagementComponent implements OnInit {
  private dialog = inject(MatDialog);
  slideService = inject(SliderManagementService)
  // @ViewChild(MatSort) sort!: MatSort;
  // @ViewChild(MatPaginator) paginator!: MatPaginator;
  private destroyRef = inject(DestroyRef);
  loading = signal(true);

  tableData = computed(() => {
    const slides = this.slideService.slider();

    if (slides.length === 0) return [];
    return slides.map((slide: {
      slideId: any;
      order: number;
      title: string;
      link: string;
      imagePath: string;
      start: string | number | Date;
      end: string | number | Date;
    }) => {

      let status = 'unknown';
      const now = new Date();
      const start = new Date(slide.start)
      const end = new Date(slide.end)

      if (now > start && now < end) {
        status = 'active'
      } else if (now < start) {
        status = 'pending'
      } else if (now > end) {
        status = 'finished'
      }

      return {
        slideId: slide.slideId || 0,
        order: slide.order || 0,
        title: slide.title || '',
        link: slide.link || '',
        imagePath: slide.imagePath || '',
        start: slide.start ? new Date(slide.start).toLocaleDateString('de-DE') : '',
        end: slide.end ? new Date(slide.end).toLocaleDateString('de-DE') : '',
        status: status || 'unknown'
      }
    });
  });
  dataSlides = new MatTableDataSource();
  displayedColumnsSlides: string[] = ['status', 'title', 'link', 'image', 'date', 'order', 'actions'];

  constructor() {
    effect(() => {
      const data = this.tableData();
      if (data.length > 0) {
        this.dataSlides.data = data;
      }
    });
  }

  async ngOnInit() {
    await this.loadSlides();
  }

  openSliderForm(element?: NotificationsModel) {
    this.dialog.open(SliderFormComponent, {
      width: '500px',
      disableClose: true,
      data: element
    });
  }

  editSlide(element: SliderManagementModel) {
    const slideToEdit = this.slideService.slider().find(slide => slide.slideId === element.slideId);

    if (slideToEdit) {
      this.dialog.open(SliderFormComponent, {
        width: '500px',
        disableClose: true,
        data: {slide: slideToEdit}
      });
    } else {
      console.error('Slide not found with ID:', element.slideId);
    }
  }

  deleteSlide(element: SliderManagementModel) {
    const dialogData = new ConfirmationDialogModel('Löschen', 'Möchten Sie dieses Element wirklich löschen?');
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      closeOnNavigation: true,
      data: dialogData
    });

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => this.loading.set(true)),
        switchMap(() => this.slideService.deleteSlide(element)),
        switchMap(() => this.slideService.downloadList(true)),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (error: any) => console.error('Deleting error:', error)
      });
  }

  private async loadSlides() {
    this.loading.set(true);
    try {
      await this.slideService.downloadList(true);
    } catch (error) {
      console.error('Error loading slides:', error);
    } finally {
      this.loading.set(false);
    }
  }

  showImagePreview(imagePath: string) {
    this.dialog.open(PreviewDialogComponent, {
      width: '60vw',
      data: {
        imagePath: imagePath
      }
    });
  }
}
